import { ref, computed, type Ref } from "vue";
import {
  type Gallery,
  type GalleryManifest,
  buildGallery
} from "@/models/gallery";

const galleries: Ref<Gallery[]> = ref([]);
const isLoading = ref(false);
const error = ref<string | null>(null);
let fetched = false;

async function fetchGalleries(): Promise<void> {
  if (fetched) return;
  isLoading.value = true;
  error.value = null;

  try {
    const indexResponse = await fetch("/gallery/index.json");
    if (!indexResponse.ok) throw new Error(`Failed to fetch gallery index: ${indexResponse.status}`);
    const galleryIds: string[] = await indexResponse.json();

    const manifests = await Promise.all(
      galleryIds.map(async (id) => {
        const response = await fetch(`/gallery/${id}.json`);
        if (!response.ok) throw new Error(`Failed to fetch gallery ${id}: ${response.status}`);
        return response.json() as Promise<GalleryManifest>;
      })
    );

    galleries.value = manifests.map(buildGallery);
    fetched = true;
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Unknown error";
  } finally {
    isLoading.value = false;
  }
}

export function useGalleries() {
  if (!fetched) {
    fetchGalleries();
  }

  return {
    galleries: computed(() => galleries.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    refresh: () => {
      fetched = false;
      return fetchGalleries();
    }
  };
}
